// API Configuration and utilities for Django backend integration
import { 
  ApiResponse, User, SSOAuthCode, AuthTokens, 
  SSOUserData, CourseResponse,
  EnrollmentResponse, ExamResponse, CertificateResponse,
  LeaderboardEntryResponse, UserBadgeResponse,
  DashboardOverview,
  AssignmentResponse, AssignmentSubmissionResponse,
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// API Client class
class ApiClient {
  private baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

    const config: RequestInit = {
      ...options,
      headers: {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...options.headers,
      },
    };

    // Add auth token if available
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers = {
          ...config.headers,
          'Authorization': `Bearer ${token}`,
        };
      }
    }

    try {
      const response = await fetch(url, config);
      if (response.status === 204 || response.status === 205) {
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Redirect to login if we get a 401 (Forbidden) error
        if (response.status === 401) {
          tokenManager.clearTokens();
          window.location.href = '/auth/login';
          throw new Error('Authentication required. Please log in again.');
        }
        throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`);
      }

      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  // SSO App Integration Methods - OAuth2 Authorization Code Flow
  async exchangeAuthCode(authCode: SSOAuthCode): Promise<ApiResponse<AuthTokens>> {
    return this.request<AuthTokens>('/users/sso/exchange-code/', {
      method: 'POST',
      body: JSON.stringify(authCode),
    });
  }

  async exchangeSchoolms(code: string): Promise<ApiResponse<AuthTokens>> {
    const response = await fetch(`${this.baseURL}/users/schoolms/exchange/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      return { success: false, error: data.error || data.detail || 'Sign-in failed' };
    }
    return { success: true, data };
  }

  // Legacy SSO Methods (keeping for backward compatibility)
  async createOrUpdateSSOUser(userData: SSOUserData): Promise<ApiResponse<AuthTokens>> {
    return this.request<AuthTokens>('/users/sso/create-or-update/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async checkSSOUserExists(ssoId: string): Promise<ApiResponse<{ exists: boolean; user?: User }>> {
    return this.request<{ exists: boolean; user?: User }>(`/users/sso/user-info/${ssoId}/`);
  }

  async ssoLogin(ssoId: string): Promise<ApiResponse<AuthTokens>> {
    return this.request<AuthTokens>('/users/sso/login/', {
      method: 'POST',
      body: JSON.stringify({ sso_id: ssoId }),
    });
  }

  // Traditional Authentication Methods
  async register(userData: {
    username: string;
    email: string;
    password: string;
    role: string;
  }): Promise<ApiResponse<AuthTokens>> {
    return this.request<AuthTokens>('/users/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(credentials: { username: string; password: string }): Promise<ApiResponse<AuthTokens>> {
    return this.request<AuthTokens>('/users/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async getProfile(ssoId: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/users/sso/user-info/${ssoId}/`);
  }

  async getCurrentUserProfile(): Promise<ApiResponse<User>> {
    return this.request<User>('/users/profile/');
  }

  async updateProfile(userData: Partial<User>): Promise<ApiResponse<User>> {
    return this.request<User>('/users/profile/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  // Course Management Methods
  async getCourses(): Promise<ApiResponse<CourseResponse>> {
    return this.request<CourseResponse>('/courses/courses/');
  }

  async getCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/`);
  }

  async createCourse(courseData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/courses/', {
      method: 'POST',
      body: JSON.stringify(courseData),
    });
  }

  // Enrollment Methods
  async enrollInCourse(courseId: number): Promise<ApiResponse<any>> {
    return this.request<any>('/enrollment/enrollments/', {
      method: 'POST',
      body: JSON.stringify({ course_id: courseId }),
    });
  }

  async getEnrollments(): Promise<ApiResponse<EnrollmentResponse>> {
    return this.request<EnrollmentResponse>('/enrollment/enrollments/');
  }

  // Exam Methods
  async getExams(): Promise<ApiResponse<ExamResponse>> {
    return this.request<ExamResponse>('/exams/exams/');
  }

  async submitExam(examId: number, answers: any[], attemptId?: number | null): Promise<ApiResponse<any>> {
    const body: { exam: number; answers: any[]; attempt?: number } = {
      exam: examId,
      answers,
    };
    if (attemptId) body.attempt = attemptId;
    return this.request<any>('/exams/submissions/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async startExam(examId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/exams/exams/${examId}/start/`, {
      method: 'POST',
    });
  }

  // Health Check
  async healthCheck(): Promise<ApiResponse<any>> {
    return this.request<any>('/health/');
  }

  async getApiStatus(): Promise<ApiResponse<any>> {
    return this.request<any>('/status/');
  }

  // Enhanced Course Management Methods
  async updateCourse(id: number, courseData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(courseData),
    });
  }

  async deleteCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/`, {
      method: 'DELETE',
    });
  }

  // Section Management
  async getSections(courseId: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/courses/sections/?course=${courseId}`, {
      method: 'GET',
    });
  }

  async getSection(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/sections/${id}/`);
  }

  async createSection(sectionData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/sections/', {
      method: 'POST',
      body: JSON.stringify(sectionData),
    });
  }

  async updateSection(id: number, sectionData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/sections/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(sectionData),
    });
  }

  async deleteSection(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/sections/${id}/`, {
      method: 'DELETE',
    });
  }

  // Lesson Management
  async getLessons(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/courses/lessons/');
  }

  async getLesson(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/`);
  }

  async createLesson(lessonData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/lessons/', {
      method: 'POST',
      body: JSON.stringify(lessonData),
    });
  }

  async updateLesson(id: number, lessonData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(lessonData),
    });
  }

  async patchLesson(id: number, lessonData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(lessonData),
    });
  }

  async deleteLesson(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/`, {
      method: 'DELETE',
    });
  }

  async getLessonsBySection(sectionId: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/courses/lessons/?section=${sectionId}`);
  }

  async publishLesson(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/publish/`, { method: 'POST' });
  }

  async unpublishLesson(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/unpublish/`, { method: 'POST' });
  }

  async reorderSections(orderedIds: number[]): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/sections/reorder/', {
      method: 'POST',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
  }

  async reorderLessons(orderedIds: number[]): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/lessons/reorder/', {
      method: 'POST',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
  }

  async publishCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/publish/`, { method: 'POST' });
  }

  async unpublishCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/unpublish/`, { method: 'POST' });
  }

  async archiveCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/archive/`, { method: 'POST' });
  }

  async restoreCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/restore/`, { method: 'POST' });
  }

  async cloneCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/clone/`, { method: 'POST' });
  }

  async submitCourseForReview(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/submit-for-review/`, { method: 'POST' });
  }

  async approveCourse(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/approve/`, { method: 'POST' });
  }

  async rejectCourse(id: number, note = ''): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  async getCourseVersions(id: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/courses/courses/${id}/versions/`);
  }

  async getCourseVersion(id: number, versionNumber: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/courses/${id}/versions/${versionNumber}/`);
  }

  async cloneSection(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/sections/${id}/clone/`, { method: 'POST' });
  }

  async cloneLesson(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/clone/`, { method: 'POST' });
  }

  async moveLesson(id: number, sectionId: number, order?: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/lessons/${id}/move/`, {
      method: 'POST',
      body: JSON.stringify({ section_id: sectionId, ...(order != null ? { order } : {}) }),
    });
  }

  async getAdminReports(): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/reports/overview/');
  }

  // Lesson Blocks
  async getBlocks(lessonId: number): Promise<ApiResponse<any[]>> {
    return this.request<any[]>(`/courses/blocks/?lesson=${lessonId}`);
  }

  async createBlock(blockData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/blocks/', {
      method: 'POST',
      body: JSON.stringify(blockData),
    });
  }

  async updateBlock(id: number, blockData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/blocks/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(blockData),
    });
  }

  async deleteBlock(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/blocks/${id}/`, { method: 'DELETE' });
  }

  async reorderBlocks(orderedIds: number[]): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/blocks/reorder/', {
      method: 'POST',
      body: JSON.stringify({ ordered_ids: orderedIds }),
    });
  }

  // Media Assets
  async getMediaAssets(folder?: string | null, assetType?: 'image' | 'video' | 'file'): Promise<ApiResponse<any[]>> {
    const qs = new URLSearchParams();
    if (folder === '') qs.set('folder', '__uncategorized__');
    else if (folder != null && folder !== '__all__') qs.set('folder', folder);
    if (assetType) qs.set('asset_type', assetType);
    const q = qs.toString();
    return this.request<any[]>(`/courses/media/${q ? `?${q}` : ''}`);
  }

  async getMediaFolders(): Promise<ApiResponse<any>> {
    return this.request<any>('/courses/media/folders/');
  }

  async createMediaAsset(formData: FormData): Promise<ApiResponse<any>> {
    const token = localStorage.getItem('access_token');
    const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/courses/media/`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (res.status === 204) return { success: true };
    const data = await res.json().catch(() => ({}));
    return res.ok ? { success: true, data } : { success: false, error: data?.detail || 'Upload failed' };
  }

  async updateMediaAsset(
    id: number,
    data: { original_filename?: string; folder?: string }
  ): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/media/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMediaAsset(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/courses/media/${id}/`, { method: 'DELETE' });
  }

  // Workspaces / Organizations / Invites
  async ensurePersonalOrganization(): Promise<ApiResponse<any>> {
    return this.request<any>('/workspaces/organizations/ensure-personal/', { method: 'POST' });
  }

  async createInstitution(name: string): Promise<ApiResponse<any>> {
    return this.request<any>('/workspaces/organizations/create-institution/', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async getOrganizations(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/workspaces/organizations/');
  }

  async getOrganization(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/workspaces/organizations/${id}/`);
  }

  async getWorkspaces(organizationId?: number): Promise<ApiResponse<any[]>> {
    const q = organizationId != null ? `?organization=${organizationId}` : '';
    return this.request<any[]>(`/workspaces/spaces/${q}`);
  }

  async createWorkspace(data: { organization: number; name: string; description?: string }): Promise<ApiResponse<any>> {
    return this.request<any>('/workspaces/spaces/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getInvites(organizationId?: number): Promise<ApiResponse<any[]>> {
    const q = organizationId != null ? `?organization=${organizationId}` : '';
    return this.request<any[]>(`/workspaces/invites/${q}`);
  }

  async createInvite(data: {
    organization_id: number;
    workspace_id?: number | null;
    email?: string;
    max_uses?: number;
    expires_days?: number | null;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/workspaces/invites/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeInvite(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/workspaces/invites/${id}/`, { method: 'DELETE' });
  }

  async getInvitePreview(token: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/workspaces/invites/by-token/${token}/`);
  }

  async acceptInvite(token: string): Promise<ApiResponse<any>> {
    return this.request<any>(`/workspaces/invites/accept/${token}/`, { method: 'POST' });
  }

  // Audit Log
  async getAuditLog(params?: { entity_type?: string; entity_id?: number }): Promise<ApiResponse<any[]>> {
    const qs = new URLSearchParams();
    if (params?.entity_type) qs.set('entity_type', params.entity_type);
    if (params?.entity_id) qs.set('entity_id', String(params.entity_id));
    const q = qs.toString();
    return this.request<any[]>(`/courses/audit/${q ? `?${q}` : ''}`);
  }

  // Enhanced Enrollment Methods
  async getEnrollment(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/enrollments/${id}/`);
  }

  async updateEnrollment(id: number, enrollmentData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/enrollments/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(enrollmentData),
    });
  }

  async deleteEnrollment(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/enrollments/${id}/`, {
      method: 'DELETE',
    });
  }

  // Progress Tracking Methods
  async getProgressTrackers(): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('/enrollment/progress/');
  }

  async getProgressTracker(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/progress/${id}/`);
  }

  async createProgressTracker(progressData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/enrollment/progress/', {
      method: 'POST',
      body: JSON.stringify(progressData),
    });
  }

  async completeLesson(progressId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/progress/${progressId}/complete/`, {
      method: 'POST',
    });
  }

  async incompleteLesson(progressId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/progress/${progressId}/incomplete/`, {
      method: 'POST',
    });
  }

  async updateLessonTime(progressId: number, timeSpent: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/progress/${progressId}/update-time/`, {
      method: 'POST',
      body: JSON.stringify({ time_spent: timeSpent }),
    });
  }

  async getProgressSummary(): Promise<ApiResponse<any>> {
    return this.request<any>('/enrollment/progress/summary/');
  }

  async getDashboardOverview(): Promise<ApiResponse<DashboardOverview>> {
    return this.request<DashboardOverview>('/dashboard/overview/');
  }

  async getEnrollmentProgress(enrollmentId: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/enrollments/${enrollmentId}/progress/`);
  }

  // Certificate Methods
  async getCertificates(): Promise<ApiResponse<CertificateResponse>> {
    return this.request<CertificateResponse>('/enrollment/certificates/');
  }

  async getCertificate(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/enrollment/certificates/${id}/`);
  }

  async getAssignments(courseId?: number, sectionId?: number): Promise<ApiResponse<AssignmentResponse>> {
    const params = new URLSearchParams();
    if (courseId) params.set('course', String(courseId));
    if (sectionId) params.set('section', String(sectionId));
    const q = params.toString() ? `?${params.toString()}` : '';
    return this.request<AssignmentResponse>(`/assignments/assignments/${q}`);
  }

  async createAssignment(data: {
    course: number;
    title: string;
    description?: string;
    section?: number | null;
    due_at?: string | null;
    passing_score?: number | null;
  }): Promise<ApiResponse<any>> {
    return this.request<any>('/assignments/assignments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteAssignment(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/assignments/assignments/${id}/`, {
      method: 'DELETE',
    });
  }

  async submitAssignment(assignmentId: number, payload: { text?: string; file?: File | null }): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('assignment', String(assignmentId));
    form.append('text', payload.text || '');
    if (payload.file) {
      form.append('file', payload.file);
    }
    return this.request<any>('/assignments/submissions/', {
      method: 'POST',
      body: form,
    });
  }

  async getAssignmentSubmissions(assignmentId: number): Promise<ApiResponse<AssignmentSubmissionResponse>> {
    return this.request<AssignmentSubmissionResponse>(
      `/assignments/submissions/?assignment=${assignmentId}`
    );
  }

  async gradeAssignment(submissionId: number, grade: number, feedback = ''): Promise<ApiResponse<any>> {
    return this.request<any>(`/assignments/submissions/${submissionId}/grade/`, {
      method: 'POST',
      body: JSON.stringify({ grade, feedback }),
    });
  }

  // Enhanced Exam Methods
  async getExam(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/exams/exams/${id}/`);
  }

  async createExam(examData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/exams/exams/', {
      method: 'POST',
      body: JSON.stringify(examData),
    });
  }

  async updateExam(id: number, examData: any): Promise<ApiResponse<any>> {
    return this.request<any>(`/exams/exams/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(examData),
    });
  }

  async deleteExam(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/exams/exams/${id}/`, {
      method: 'DELETE',
    });
  }

  async getSubmission(id: number): Promise<ApiResponse<any>> {
    return this.request<any>(`/exams/submissions/${id}/`);
  }

  // Gamification Methods
  async getLeaderboard(): Promise<ApiResponse<LeaderboardEntryResponse>> {
    return this.request<LeaderboardEntryResponse>('/gamification/leaderboard/');
  }

  async getUserBadges(): Promise<ApiResponse<UserBadgeResponse>> {
    return this.request<UserBadgeResponse>('/gamification/badges/');
  }

  // SSO Configuration
  async getSSOConfig(): Promise<ApiResponse<any>> {
    return this.request<any>('/users/config/');
  }

  // Test Authentication
  async testAuth(): Promise<ApiResponse<any>> {
    return this.request<any>('/users/test-auth/');
  }
}

// Create and export the API client instance
export const apiClient = new ApiClient();

// Utility functions for token management
export const tokenManager = {
  setTokens(tokens: AuthTokens) {
    if (typeof window !== 'undefined') {
      localStorage.setItem('access_token', tokens.access);
      localStorage.setItem('refresh_token', tokens.refresh);
      localStorage.setItem('user', JSON.stringify(tokens.user));
    }
  },

  getAccessToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('access_token');
    }
    return null;
  },

  getRefreshToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('refresh_token');
    }
    return null;
  },

  getUser(): User | null {
    if (typeof window !== 'undefined') {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    }
    return null;
  },

  clearTokens() {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  isAuthenticated(): boolean {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('access_token');
    }
    return false;
  },
}; 